<template>
    <FormView title="User Management" 
        :mainFormClass="mainFormClass">
        <template #mainForm>
                <div class="max-w-full">
                    <FormContainer class="w-[300px] max-w-full">
                        <Input :placeholder="'Search...'" v-model="filter.search" @input="onInput"/>
                    </FormContainer>
                </div>
                <div class="max-w-full flex items-center justify-between gap-4 pb-2">
                    <div class="inline-flex rounded-md shadow-xs cursor-pointer">
                        <a @click="filterList({isActivated: true})" aria-current="page" 
                            :class="{'text-indigo-700': filter.isActivated==true}"
                            class="px-4 py-2 text-sm font-medium bg-white border border-gray-200 rounded-s-lg hover:bg-gray-100 focus:z-10 focus:ring-2 focus:ring-indigo-700 focus:text-indigo-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:text-white dark:hover:bg-gray-700 dark:focus:ring-indigo-500 dark:focus:text-white">
                            Active
                        </a>
                        <a @click="filterList({isActivated: false})" 
                            :class="{'text-indigo-700': filter.isActivated==false}"
                            class="px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-e-lg hover:bg-gray-100 hover:text-indigo-700 focus:z-10 focus:ring-2 focus:ring-indigo-700 focus:text-indigo-700 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:text-white dark:hover:bg-gray-700 dark:focus:ring-indigo-500 dark:focus:text-white">
                            Deactive
                        </a>
                    </div>
                    <button
                        v-if="selectedUsers.length > 0"
                        @click="deleteSelectedUsers"
                        class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                        :disabled="deleting"
                    >
                        {{ deleting ? 'Deleting...' : `Delete Selected (${selectedUsers.length})` }}
                    </button>
                </div>
                <Loading v-if="loading"/>
                <div v-else class="max-w-full overflow-auto">
                    <Table  :total="users.total">
                        <Thead >
                            <HeadColumn>
                                <input 
                                    type="checkbox" 
                                    class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                    :checked="selectedUsers.length === users.data.length && users.data.length > 0"
                                    @change="toggleSelectAll"
                                />
                            </HeadColumn>
                            <HeadColumn v-for="h in usersTable.columns" :key="h.title" :title="h.title" :field="h.field" :class="h.rowStyle" ></HeadColumn>
                        </Thead>
                        <TableRow :definition="usersTable.columns" :data="users.data" :selected-users="selectedUsers" @toggle-user-selection="toggleUserSelection"/>
                    </Table>
                    <TriadPagination class="pt-2" :filter="filter" :totalPages="totalPages" @change="onPaginationChange"/>
                </div>
        </template>
    </FormView>
</template>
<script setup>
import FormView from '@/components/common/TriadFormView.vue';
import TriadActivateIcon from "@/components/icons/TriadActivateIcon.vue";
import TriadRemoveIcon from "@/components/icons/TriadRemoveIcon.vue";
import { onMounted, ref, shallowRef } from "vue";
import { debounce } from "@/service/debounce";
import Pagination from '@/models/pagination'
import toastStore from '@/store/toast.store';
import dialogStore from '@/store/dialog.store';

const loading = ref(true);
const users = ref({data:[], total: 0, totalPages:0});
const totalPages = ref(0);
const originalList = ref([]);
const selectedUsers = ref([]);
const deleting = ref(false);

const filter = ref({
    pagination: new Pagination(1,10)
});

const activateUser = (element) => {
    const url  =`/api/admin/users/${element.id}/`
    fetch(url+(element.isActivated?'deactivate':'activate')).then( async (r) => {
        const response = await r.json();
        toastStore.dispatch('addMessage', {message: response.message, icon: 'success'})
        fetchData()
    }).catch(error => {
        toastStore.dispatch('addMessage', {message: error.message, icon: 'error', ttl: 3000})
    })
}
const removeUser = async (element) => {
    if (await dialogStore.dispatch('confirm', 'Are you sure you want to remove this user?')) {
        fetch(`/api/admin/users/${element.id}`,
            {method: 'DELETE'}
        ).then( async (r) => {
            const response = await r.json();
            toastStore.dispatch('addMessage', {message: response.message, icon: 'success'})
            fetchData()
        }).catch(error => {
            toastStore.dispatch('addMessage', {message: error.message, icon: 'error', ttl: 3000})
        })
    }
}

const formatDate = (date) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric'}
    return new Date(date).toLocaleDateString('en', options)
}

const toggleUserSelection = (userId) => {
    const index = selectedUsers.value.indexOf(userId);
    if (index > -1) {
        selectedUsers.value.splice(index, 1);
    } else {
        selectedUsers.value.push(userId);
    }
};

const toggleSelectAll = (event) => {
    if (event.target.checked) {
        selectedUsers.value = users.value.data.map(u => u.id);
    } else {
        selectedUsers.value = [];
    }
};

const deleteSelectedUsers = async () => {
    if (selectedUsers.value.length === 0) return;
    
    const count = selectedUsers.value.length;
    if (!await dialogStore.dispatch('confirm', `Are you sure you want to delete ${count} user(s)?`)) {
        return;
    }
    
    deleting.value = true;
    
    try {
        const response = await fetch('/api/admin/users', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ userIds: selectedUsers.value })
        });
        
        const result = await response.json();
        
        if (response.ok) {
            toastStore.dispatch('addMessage', {
                message: result.message || `Successfully deleted ${count} user(s)`,
                icon: 'success'
            });
            selectedUsers.value = [];
            fetchData();
        } else {
            toastStore.dispatch('addMessage', {
                message: result.error || 'Failed to delete users',
                icon: 'error',
                ttl: 5000
            });
        }
    } catch (error) {
        toastStore.dispatch('addMessage', {
            message: error.message || 'Failed to delete users',
            icon: 'error',
            ttl: 5000
        });
    } finally {
        deleting.value = false;
    }
};

const usersTable = shallowRef({
    columns: [
        { title: 'Name', field: 'name', rowStyle: ''},
        { title: 'E-mail', field: 'email', rowStyle: ''},
        { title: 'Created at', field: 'createdAt', format: (element) => formatDate(element.createdAt), rowStyle: 'hidden md:table-cell'},
        { title: 'Active', action: activateUser, 
            component: TriadActivateIcon, 
            properties: (element) => ({
                activated: element.isActivated, 
                tooltip:  element.isActivated? 'Activated': 'Deactivated'
            })},
        {   action: removeUser, 
            component: TriadRemoveIcon, 
            properties: (element) => ({activated: element.isActivated }), 
            rowStyle: 'hidden md:table-cell'}
    ]
});

const fetchData = async () => {
    try {
        const query = Object.entries(filter.value).map( f => {
            if (typeof(f[1]) == 'object'){
                return f[1]
            } else {
                return f[0]+'='+f[1]
            }
        }).join('&')
        //loading.value=true
        let response = await fetch('/api/admin/users?'+query);
        users.value = await response.json();
        totalPages.value = users.value.totalPages;
        originalList.value = users.value
        //loading.value=false
    } catch (error) {
        console.error('Error fetching dashboard data:', error);
    }
};

const filterList = (q) => {
    if (filter.value.isActivated==q.isActivated){
        delete filter.value.isActivated
        delete q.isActivated
    }
    Object.entries(q).forEach(element => {
        filter.value[element[0]] = element[1]
    })
    fetchData();
}

onMounted(() => {
    loading.value = true;
    fetchData();
    loading.value = false;
});
const onInput = debounce(() => {
    fetchData();
}, 500);

const onPaginationChange = () => {
    fetchData();
}


</script>